import type { JSX } from "react";
import { classNames } from "@/utils/classnames";
import React, { forwardRef } from "react";

const sizes = {
  sm: "h-6",
  md: "h-8",
  lg: "h-10",
  xl: "h-12",
  "2xl": "h-16",
  "3xl": "h-20",
};

export interface LogoTypeProps extends React.ComponentProps<"svg"> {
  size?: keyof typeof sizes;
}

export const LogoTypeV2 = forwardRef<SVGSVGElement, React.ComponentProps<"svg">>(
  function LogoTypeV2(
    { color, className, ...rest }: { size?: keyof typeof sizes } & React.ComponentProps<"svg">,
    ref,
  ): JSX.Element {
    return (
      <svg
        ref={ref}
        width="91"
        height="21"
        viewBox="0 0 91 21"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
        fill={color}
        className={className}
      >
        <path
          d="M7.21596 20.9596C2.81536 20.9596 0 17.3059 0 13.1743C0 9.04284 2.72383 5.38909 7.09698 5.38909C10.6006 5.38909 13.2348 7.30566 13.8041 10.8386H10.5109C10.1521 8.92202 8.92385 7.81455 7.15739 7.81455C4.73193 7.81455 3.44506 10.0606 3.44506 13.1743C3.44506 16.2881 4.82162 18.5342 7.30749 18.5342C9.04467 18.5342 10.3022 17.3663 10.6317 15.4497H13.956C13.3867 18.9826 10.7233 20.9596 7.21962 20.9596H7.21596ZM16.8885 20.6594V0H20.0626V8.62364H20.1231C20.7509 6.52768 22.3087 5.38909 24.4944 5.38909C27.7875 5.38909 29.4057 7.63516 29.4057 10.9283V20.6594H26.1418V11.8857C26.1418 9.10141 25.1534 7.84384 23.1471 7.84384C21.1408 7.84384 20.1523 9.2204 20.1523 11.677V20.6594H16.8885ZM37.6394 20.9596C34.5257 20.9596 32.34 19.0138 32.34 16.2881C32.34 13.2036 35.0346 12.0669 38.3588 11.7063L42.1627 11.3164V10.8679C42.1627 8.86161 41.0241 7.81272 39.1386 7.81272C37.2532 7.81272 36.2354 8.7408 36.1439 10.4176H32.88C32.999 7.51251 35.3348 5.38726 39.1972 5.38726C43.0596 5.38726 45.4247 7.42281 45.4247 11.4665V20.6594H42.2798V17.7251H42.1901C41.5623 19.7917 39.8837 20.9596 37.6394 20.9596ZM38.4778 18.5342C40.8136 18.5342 42.1608 16.9471 42.1608 14.1024V13.5935L38.897 13.9523C36.92 14.161 35.7833 14.8804 35.7833 16.3174C35.7833 17.7543 37.0408 18.5323 38.4778 18.5323V18.5342ZM49.2889 0H52.5528V20.6594H49.2889V0ZM59.8273 12.8449V20.6594H56.5635V0H59.8273V11.8582L65.9944 5.6893H70.0362L63.4792 12.2774L70.3364 20.6612H66.2342L59.8273 12.8467V12.8449ZM90.2984 5.6893H75.3265V20.6612H90.2984V5.6893Z"
          fill="currentColor"
        />
      </svg>
    );
  },
);

export const LogoType = forwardRef<SVGSVGElement, LogoTypeProps>(function LogoType(
  {
    size = "md",
    color,
    className,
    ...rest
  }: { size?: keyof typeof sizes } & React.ComponentProps<"svg">,
  ref,
): JSX.Element {
  return (
    <svg
      version="1.1"
      id="Layer_1"
      x="0px"
      y="0px"
      viewBox="42 0 577 194.68"
      xmlns="http://www.w3.org/2000/svg"
      fill={color}
      className={classNames(sizes[size], className)}
      {...rest}
      ref={ref}
    >
      <g>
        <g>
          <g>
            <path d="M81.4,154.59c-24.04,0-39.42-19.96-39.42-42.53c0-22.08,14.88-42.53,38.77-42.53c19.14,0,33.53,10.47,36.64,29.77 H99.39c-1.96-10.47-8.67-16.52-18.32-16.52c-13.25,0-20.28,12.27-20.28,29.28c0,17.18,7.52,29.28,21.1,29.28 c9.49,0,16.36-6.38,18.16-16.85h18.16C115.1,143.8,100.54,154.59,81.4,154.59z" />
            <path d="M134.24,152.96V40.09h17.34V87.2h0.33c3.43-11.45,11.94-17.67,23.88-17.67c17.99,0,26.83,12.27,26.83,30.26v53.16 h-17.83v-47.93c0-15.21-5.4-22.08-16.36-22.08c-10.96,0-16.36,7.52-16.36,20.94v49.07H134.24z" />
            <path d="M247.6,154.59c-17.01,0-28.95-10.63-28.95-25.52c0-16.85,14.72-23.06,32.88-25.03l20.78-2.13v-2.45 c0-10.96-6.22-16.69-16.52-16.69c-9.98,0-15.86,5.07-16.36,14.23h-17.83c0.65-15.87,13.41-27.48,34.51-27.48 c20.78,0,34.02,11.12,34.02,33.21v50.22h-17.18v-16.03h-0.49C269.03,148.21,259.87,154.59,247.6,154.59z M252.18,141.34 c12.76,0,20.12-8.67,20.12-24.21v-2.78l-17.83,1.96c-10.8,1.14-17.01,5.07-17.01,12.92 C237.46,136.93,244.33,141.34,252.18,141.34z" />
            <path d="M311.24,40.09h17.83v112.86h-17.83V40.09z" />
            <path d="M368.81,110.26v42.69h-17.83V40.09h17.83v64.78l33.69-33.7h22.08l-35.82,35.99l37.46,45.8h-22.41L368.81,110.26z" />
          </g>
        </g>
        <rect x="453.49" y="71.17" width="81.79" height="81.79" />
      </g>
    </svg>
  );
});

export interface LogoSquareProps extends React.ComponentProps<"svg"> {
  dark?: boolean;
  size?: keyof typeof sizes;
}

export const LogoSquare = forwardRef<SVGSVGElement, LogoSquareProps>(function LogoSquare(
  { dark, size = "md", className, ...rest },
  ref,
): JSX.Element {
  return (
    <svg
      version="1.1"
      id="Layer_1"
      x="0px"
      y="0px"
      viewBox="0 0 81.79 81.79"
      xmlns="http://www.w3.org/2000/svg"
      className={classNames(
        sizes[size],
        dark === true && "fill-white",
        dark === false && "fill-black",
        dark == null && "fill-black dark:fill-white",
        className,
      )}
      {...rest}
      ref={ref}
    >
      <g>
        <rect width="81.79" height="81.79" />
      </g>
    </svg>
  );
});
